/**
 * Vermont State-Specific Diagnostic Questions
 * COMPLETE REBUILD - Using correct regulations
 * 
 * CRITICAL CORRECTION:
 * Original file cited "CVR 13-110-12" throughout - this is WRONG
 * CVR 13-110-12 = Therapeutic Community Residences (psychiatric/mental health facilities)
 * 
 * CORRECT REGULATION: 13-110-010 Code Vt. R.
 * "Designation and Operation of Home Health Agencies"
 * 
 * KEY DIFFERENCES FROM ORIGINAL FILE:
 * 1. Regulation Code: 13-110-010 (not 13-110-12)
 * 2. Terminology: Vermont uses "Designation" not "License"
 * 3. Designation Period: 4 years (not 1 year as stated in original)
 * 4. Record Retention: 10 years (not 7 years as stated in original)
 * 5. Prerequisites: CON + Medicare certification required BEFORE designation
 * 6. Regulatory Body: DAIL Division of Licensing and Protection (DLP)
 * 
 * STATUTORY AUTHORITY:
 * - 33 V.S.A. § 6303(a)
 * - 18 V.S.A. Chapter 221
 * - 33 V.S.A. Chapter 63
 * 
 * VERIFIED SOURCES:
 * - https://www.law.cornell.edu/regulations/vermont/13-010-Code-Vt-R-13-110-010-X
 * - https://dail.vermont.gov/regulations-designation-operation-hha
 * - https://dlp.vermont.gov/aps/adult-abuse-registry
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const VERMONT_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: DESIGNATION & OPERATIONS
  // ============================================
  {
    id: 'VT-A1',
    itemId: 'A1',
    stateCode: 'VT',
    title: 'Home Health Agency Designation Status',
    description: 'Vermont requires home health agencies to be designated by DAIL per 13-110-010',
    severity: 'high',
    regulationCode: '13-110-010-4',
    regulationName: 'License',
    agencyName: 'Vermont Department of Disabilities, Aging and Independent Living',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/regulations-designation-operation-hha',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active home health agency DESIGNATION from DAIL?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Vermont uses "designation" not "license" - designations are valid for 4 years per Section 4.10',
      },
      {
        id: 'q2',
        question: 'What is your designation expiration date?',
        inputType: 'date',
        helpText: 'Renewal applications must be submitted at least 60 days before expiration per Section 5.1',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is your proof of designation posted in a visible location?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 4.12',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 120, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Vermont home health agency designation is current and active per 13-110-010.',
      atRisk: 'Your designation expires within 120 days. Submit renewal application to DAIL at least 60 days before expiration per Section 5.1.',
      nonCompliant: 'Operating a home health agency in Vermont without valid DAIL designation violates 33 V.S.A. § 6303. Contact DAIL immediately at (802) 241-2401.',
      unknown: 'Verify your home health agency designation status with DAIL at (802) 241-2401.',
    },
    stateNotes: [
      'Vermont uses "designation" not "license" terminology',
      'Designations are valid for 4 years (Section 4.10)',
      'Renewal applications due 60 days before expiration (Section 5.1)',
      'Must post proof of designation visibly (Section 4.12)',
      'Provisional designation available for up to 1 year for initial Medicare certification (Section 4.11)',
    ],
  },

  {
    id: 'VT-A2',
    itemId: 'A2',
    stateCode: 'VT',
    title: 'Medicare Certification Prerequisite',
    description: 'Vermont requires Medicare home health certification before designation',
    severity: 'high',
    regulationCode: '13-110-010-4.2',
    regulationName: 'Medicare Certification',
    agencyName: 'Centers for Medicare and Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have current Medicare home health agency certification from CMS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Medicare certification is a prerequisite for Vermont designation per Section 4.2',
      },
      {
        id: 'q2',
        question: 'If nationally accredited and deemed, have you provided documentation to DAIL?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, documentation provided' },
          { value: 'not_accredited', label: 'Not nationally accredited' },
          { value: 'no', label: 'No, have not provided' },
        ],
        helpText: 'Must notify DAIL of accreditation status and any changes per Section 4.2',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'includes', questionId: 'q2', value: ['yes', 'not_accredited'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification is current and properly documented with DAIL per Section 4.2.',
      atRisk: 'If nationally accredited, provide documentation to DAIL and notify of any status changes.',
      nonCompliant: 'Medicare certification is a prerequisite for Vermont designation. Obtain CMS certification before applying.',
      unknown: 'Verify Medicare certification status.',
    },
    stateNotes: [
      'Medicare certification required BEFORE applying for Vermont designation',
      'Must notify DAIL of accreditation status and any changes',
      'Vermont follows Medicare Conditions of Participation for skilled services (Section 11.1)',
    ],
  },

  {
    id: 'VT-A3',
    itemId: 'A3',
    stateCode: 'VT',
    title: 'Certificate of Need (CON)',
    description: 'Vermont requires CON from Green Mountain Care Board before designation',
    severity: 'high',
    regulationCode: '13-110-010-4.1',
    regulationName: 'Certificate of Need',
    agencyName: 'Green Mountain Care Board',
    agencyAbbr: 'GMCB',
    agencyPhone: '(802) 828-2177',
    agencyUrl: 'https://gmcboard.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a valid Certificate of Need (CON) from the Green Mountain Care Board for your geographic service area?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'CON is required before applying for DAIL designation per Section 4.1',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Certificate of Need is current for your geographic service area per Section 4.1.',
      atRisk: 'CON status should be verified with Green Mountain Care Board.',
      nonCompliant: 'A Certificate of Need from the Green Mountain Care Board is required before applying for Vermont designation. Contact GMCB at (802) 828-2177.',
      unknown: 'Verify CON status with Green Mountain Care Board.',
    },
    stateNotes: [
      'CON required from GMCB before filing designation application with DAIL',
      'New CON required when >50% ownership interest is transferred (Section 14.2)',
      'CON specifies geographic service area',
    ],
  },

  {
    id: 'VT-A4',
    itemId: 'A4',
    stateCode: 'VT',
    title: 'Administrator Qualifications',
    description: 'Vermont requires a qualified administrator per 13-110-010',
    severity: 'high',
    regulationCode: '13-110-010-2.2(b)',
    regulationName: 'Administrator',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a qualified administrator who meets Vermont requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Administrator must organize/direct functions, employ qualified personnel, implement budgeting systems per Section 2.2(b)',
      },
      {
        id: 'q2',
        question: 'Is there a qualified person authorized IN WRITING to act in the administrator\'s absence?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Section 2.2(b)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications and backup designation meet 13-110-010 requirements.',
      atRisk: 'A qualified person must be authorized IN WRITING to act in the administrator\'s absence per Section 2.2(b).',
      nonCompliant: 'Your agency lacks a qualified administrator per 13-110-010 Section 2.2(b). This is a critical designation requirement.',
      unknown: 'Verify administrator qualifications meet 13-110-010 requirements.',
    },
    stateNotes: [
      'Administrator may also be the supervising physician or RN',
      'Must organize and direct ongoing functions',
      'Maintain liaison among governing body, professional personnel, and staff',
      'Employ qualified personnel and ensure adequate staff education/evaluations',
      'Implement effective budgeting and accounting system',
    ],
  },

  {
    id: 'VT-A5',
    itemId: 'A5',
    stateCode: 'VT',
    title: 'Governing Body or Consumer Advisory Board',
    description: 'Vermont requires specific governance structure based on profit status',
    severity: 'medium',
    regulationCode: '13-110-010-6',
    regulationName: 'Governing Body',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is your home health agency for-profit or not-for-profit?',
        inputType: 'select',
        options: [
          { value: 'nonprofit', label: 'Not-for-profit' },
          { value: 'forprofit', label: 'For-profit' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For NOT-FOR-PROFIT: Is the board representative of the demographic makeup of your service area with majority being patients/family members?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, meets composition requirements' },
          { value: 'no', label: 'No, does not meet requirements' },
        ],
        helpText: 'Per Section 6.2, majority must be patients/family members who received services',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'nonprofit',
        },
      },
      {
        id: 'q3',
        question: 'For FOR-PROFIT: Do you have a consumer advisory board that meets at least twice per year?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, meets requirements' },
          { value: 'no', label: 'No advisory board or meets less frequently' },
        ],
        helpText: 'Per Section 6.3, must meet at least twice per year and advise on planning and patient needs',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'forprofit',
        },
      },
      {
        id: 'q4',
        question: 'Does the board president certify composition annually to the Commissioner?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 6.2(b) or 6.3(b)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'nonprofit' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'forprofit' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Governance structure meets Section 6 requirements with proper board composition and annual certification.',
      atRisk: 'Board president must certify composition annually to the Commissioner per Section 6.2(b) or 6.3(b).',
      nonCompliant: 'Governance structure does not meet 13-110-010 Section 6 requirements. For-profit agencies must have consumer advisory boards meeting twice yearly; non-profits must have representative boards with patient/family majority.',
      unknown: 'Review governance structure against Section 6 requirements.',
    },
    stateNotes: [
      'Not-for-profit: Board must be representative of demographic makeup',
      'For-profit/multistate: Must have consumer advisory board',
      'Majority must be patients/family members who received services',
      'Consumer advisory board must meet at least twice per year',
      'Board composition confirmed by annual independent audit',
    ],
  },

  {
    id: 'VT-A6',
    itemId: 'A6',
    stateCode: 'VT',
    title: 'Written Policies and Procedures',
    description: 'Vermont requires specific written policies per Section 7.8',
    severity: 'high',
    regulationCode: '13-110-010-7.8',
    regulationName: 'Policies',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Vermont-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'admission_discharge', label: 'Admission, transfer, reduction in services and discharge' },
          { value: 'medical_supervision', label: 'Medical supervision and plans of care' },
          { value: 'emergency_care', label: 'Emergency care' },
          { value: 'patient_records', label: 'Patient records (confidentiality, retention, disclosure)' },
          { value: 'personnel', label: 'Personnel (qualifications, verification, training, evaluation)' },
          { value: 'quality_improvement', label: 'Quality improvement and program improvement' },
          { value: 'complaints', label: 'Handling complaints and grievances' },
          { value: 'electronic_records', label: 'Electronic records (data integrity, security, encryption)' },
          { value: 'supervision', label: 'Supervision of licensed and unlicensed personnel' },
          { value: 'advance_directives', label: 'Advance directives' },
        ],
        required: true,
        helpText: 'All policies are required per Section 7.8',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Vermont 13-110-010 Section 7.8 requirements.',
      atRisk: 'Your policy manual is missing required sections per Section 7.8. Missing: {missing}.',
      nonCompliant: 'Multiple required policies are missing per Section 7.8. Immediately develop written policies for: {missing}.',
      unknown: 'Review your policies against Section 7.8 requirements.',
    },
    stateNotes: [
      'All policies required per Section 7.8 must be in writing',
      'Electronic records policy must address data integrity, security, and encryption per Section 7.8(h)',
      'Supervision policies must cover both licensed and unlicensed personnel per Section 7.8(i)',
      'Policies must be reviewed annually by administrator',
      'Staff must be trained on all policies during orientation',
    ],
  },

  {
    id: 'VT-A7',
    itemId: 'A7',
    stateCode: 'VT',
    title: 'Local Community Services Plan',
    description: 'Vermont requires a local community services plan per Section 7.5',
    severity: 'medium',
    regulationCode: '13-110-010-7.5',
    regulationName: 'Community Services',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a local community services plan that includes:',
        inputType: 'checklist',
        options: [
          { value: 'needs', label: 'Home health care needs of your service area population' },
          { value: 'methods', label: 'Methods to meet those needs' },
          { value: 'schedule', label: 'Schedule for anticipated new/additional services' },
          { value: 'resources', label: 'Resources needed and available' },
          { value: 'interruption', label: 'Plan for unforeseen service interruptions' },
          { value: 'public_input', label: 'How public input was obtained' },
          { value: 'availability', label: 'How final plan is made available to public' },
        ],
        required: true,
        helpText: 'Required per Section 7.5',
      },
      {
        id: 'q2',
        question: 'Is your local community services plan revised at least every 4 years?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 7.5(b)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Local community services plan meets Section 7.5 requirements.',
      atRisk: 'Local community services plan may be incomplete or not revised within 4 years.',
      nonCompliant: 'Local community services plan does not meet Section 7.5 requirements.',
      unknown: 'Review local community services plan against Section 7.5 requirements.',
    },
    stateNotes: [
      'Plan must identify home health care needs of service area per Section 7.5(a)(1)',
      'Must describe methods to meet identified needs per Section 7.5(a)(2)',
      'Schedule for anticipated new services required per Section 7.5(a)(3)',
      'Plan must be revised at least every 4 years per Section 7.5(b)',
      'Public input process must be documented per Section 7.5(a)(6)',
    ],
  },

  {
    id: 'VT-A8',
    itemId: 'A8',
    stateCode: 'VT',
    title: 'Emergency Management Plan',
    description: 'Vermont requires an emergency management plan per Section 7.9',
    severity: 'high',
    regulationCode: '13-110-010-7.9',
    regulationName: 'Emergency Prep',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency management plan describing how you will continue services during emergencies?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes, comprehensive plan addressing service continuity' },
          { value: 'partial', label: 'Partial plan' },
          { value: 'no', label: 'No emergency management plan' },
        ],
        required: true,
        helpText: 'Required per Section 7.9',
      },
      {
        id: 'q2',
        question: 'Does your plan address individual patient needs during unexpected service disruptions?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 7.9',
      },
      {
        id: 'q3',
        question: 'Does your plan include Vermont-specific emergencies (winter weather, flooding)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Critical for Vermont given severe winter weather and flooding risks',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'comprehensive' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Emergency management plan meets Section 7.9 requirements including Vermont-specific emergencies.',
      atRisk: 'Emergency management plan is incomplete. Must address service continuity and individual patient needs during disruptions.',
      nonCompliant: 'No emergency management plan. This violates Section 7.9. Immediately develop comprehensive plan.',
      unknown: 'Review emergency management plan against Section 7.9 requirements.',
    },
    stateNotes: [
      'Plan must describe how agency will continue or arrange services during emergency/crisis/disaster',
      'Must identify how to address individual patient needs during unexpected service disruption',
      'Vermont-specific: severe winter weather, flooding, ice storms',
      'Plan must be available to DAIL upon request',
    ],
  },

  {
    id: 'VT-A9',
    itemId: 'A9',
    stateCode: 'VT',
    title: 'Insurance Coverage',
    description: 'Vermont requires specific insurance coverage per Section 9.1(d)',
    severity: 'high',
    regulationCode: '13-110-010-9.1(d)',
    regulationName: 'Insurance',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'What insurance coverage does your agency maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'fire', label: 'Fire insurance' },
          { value: 'professional', label: 'Professional liability insurance' },
          { value: 'general', label: 'General liability insurance' },
          { value: 'directors', label: 'Directors/Officers liability insurance' },
          { value: 'workers_comp', label: 'Workers\' compensation insurance' },
          { value: 'auto', label: 'Auto insurance (if transporting patients)' },
        ],
        required: true,
        helpText: 'Fire, professional liability, general liability, and D&O liability are required per Section 9.1(d)',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Insurance coverage meets Vermont Section 9.1(d) requirements.',
      atRisk: 'Insurance coverage may be incomplete. Section 9.1(d) requires fire, professional liability, general liability, and D&O liability.',
      nonCompliant: 'Insurance coverage is inadequate per Section 9.1(d). Obtain required coverage immediately.',
      unknown: 'Verify insurance coverage against Section 9.1(d) requirements.',
    },
    stateNotes: [
      'Required: Fire insurance',
      'Required: Professional liability insurance',
      'Required: General liability insurance',
      'Required: Directors/Officers liability insurance',
      'Workers\' compensation and auto recommended',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL & BACKGROUND CHECKS
  // ============================================
  {
    id: 'VT-B1',
    itemId: 'B1',
    stateCode: 'VT',
    title: 'Background Check Requirements',
    description: 'Vermont requires background checks per Section 7.6 and 33 V.S.A. § 6911',
    severity: 'high',
    regulationCode: '13-110-010-7.6',
    regulationName: 'Background Checks',
    agencyName: 'Vermont DAIL Division of Licensing and Protection',
    agencyAbbr: 'DLP',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dlp.vermont.gov/aps/adult-abuse-registry',
    questions: [
      {
        id: 'q1',
        question: 'For ALL employees, contractors, and volunteers providing direct care, which background checks do you conduct? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'adult_abuse', label: 'Vermont Adult Abuse Registry (33 V.S.A. § 6911)' },
          { value: 'child_protection', label: 'Vermont Child Protection Registry' },
          { value: 'vcic', label: 'Vermont Criminal Information Center (VCIC) check' },
          { value: 'oig', label: 'OIG Exclusion List verification' },
        ],
        required: true,
        helpText: 'Background checks required per Section 7.6 and DAIL policy',
      },
      {
        id: 'q2',
        question: 'Are background checks completed BEFORE employees have patient contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do you verify that NO employee has a substantiated record of abuse, neglect, or exploitation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, all verified' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Cannot employ anyone with substantiated record per Section 7.6',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 2 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background check process meets Section 7.6 requirements with Adult Abuse Registry, Child Protection Registry, and criminal checks.',
      atRisk: 'Background check process may be incomplete. Must include Adult Abuse Registry, Child Protection Registry, and criminal checks before patient contact.',
      nonCompliant: 'Employing workers with substantiated abuse/neglect/exploitation records violates Section 7.6. Immediately verify all employees.',
      unknown: 'Audit background check process against Section 7.6 and DAIL policy.',
    },
    stateNotes: [
      'Cannot employ anyone with substantiated abuse/neglect/exploitation record',
      'Check Adult Abuse Registry per 33 V.S.A. § 6911',
      'Check Child Protection Registry (DCF)',
      'Background checks for all employees, contractors, and volunteers with direct care',
      'Use AHS Registry Checking system: https://www.ahsnet.ahs.state.vt.us/ABC/SubscriberHome.cfm',
    ],
  },

  {
    id: 'VT-B2',
    itemId: 'B2',
    stateCode: 'VT',
    title: 'Professional License Verification',
    description: 'Vermont requires verification of all professional licenses',
    severity: 'high',
    regulationCode: '13-110-010-4.5(c)',
    regulationName: 'License Verification',
    agencyName: 'Vermont Office of Professional Regulation',
    agencyAbbr: 'OPR',
    agencyPhone: '(802) 828-1505',
    agencyUrl: 'https://sos.vermont.gov/opr/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses (RN, LPN, PT, OT, etc.) with Vermont licensing boards before hiring?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified with state boards' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you maintain copies of current licenses in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Section 4.5(c) requires copies of current licenses for administrator and DON',
      },
      {
        id: 'q3',
        question: 'Do you track license expiration dates and verify renewal?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated tracking system with alerts' },
          { value: 'manual', label: 'Manual tracking' },
          { value: 'none', label: 'No active tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'includes', questionId: 'q3', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification meets Vermont requirements.',
      atRisk: 'License verification is inconsistent. Implement systematic verification for all licensed professionals.',
      nonCompliant: 'Employing unlicensed professionals violates Vermont law. Verify all professional licenses with OPR at (802) 828-1505.',
      unknown: 'Review license verification processes.',
    },
    stateNotes: [
      'Vermont OPR license verification available at https://secure.professionals.vermont.gov/',
      'Licenses must be verified at hire and upon renewal per Section 7.8(e)(1)',
      'All healthcare professionals must hold valid Vermont licenses per 26 V.S.A.',
      'Document verification date and method in personnel file',
      'Set up automatic renewal tracking to prevent lapses',
    ],
  },

  {
    id: 'VT-B3',
    itemId: 'B3',
    stateCode: 'VT',
    title: 'Personnel Policies and Records',
    description: 'Vermont requires comprehensive personnel policies per Section 7.8(e)',
    severity: 'medium',
    regulationCode: '13-110-010-7.8(e)',
    regulationName: 'Personnel',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Which personnel documents do you maintain for EVERY employee? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'application', label: 'Employment application' },
          { value: 'credentials', label: 'Professional licenses and certifications' },
          { value: 'background', label: 'Background check results (all registries)' },
          { value: 'orientation', label: 'Orientation completion documentation' },
          { value: 'competency', label: 'Competency evaluations' },
          { value: 'training', label: 'Training records' },
          { value: 'job_desc', label: 'Job description' },
          { value: 'evaluations', label: 'Performance evaluations' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all required documentation.',
      atRisk: 'Some personnel files may be missing required documents. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation. DLP surveyors will cite incomplete files.',
      unknown: 'Audit personnel files for completeness.',
    },
    stateNotes: [
      'Personnel files must include job descriptions per Section 7.8(e)(2)',
      'Background check documentation required per 33 V.S.A. Chapter 49',
      'Competency evaluation records must be maintained per Section 7.8(e)(4)',
      'Annual performance evaluations required for all staff',
      'Files must be secured and access limited per HIPAA',
    ],
  },

  // ============================================
  // SECTION C: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'VT-C1',
    itemId: 'C1',
    stateCode: 'VT',
    title: 'Staff Orientation',
    description: 'Vermont requires comprehensive orientation before patient contact',
    severity: 'high',
    regulationCode: '13-110-010-7.8(e)',
    regulationName: 'Orientation',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new employees complete orientation before patient contact that covers:',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Agency policies and procedures' },
          { value: 'patient_rights', label: 'Patient rights' },
          { value: 'safety', label: 'Safety and emergency procedures' },
          { value: 'infection', label: 'Infection prevention' },
          { value: 'abuse', label: 'Abuse/neglect/exploitation recognition and reporting' },
          { value: 'documentation', label: 'Documentation requirements' },
          { value: 'confidentiality', label: 'Confidentiality and HIPAA' },
          { value: 'advance_directives', label: 'Advance directives (DNR, COLST)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation program meets Vermont requirements.',
      atRisk: 'Orientation may be incomplete. Missing: {missing}.',
      nonCompliant: 'Staff are providing care without adequate orientation. Immediately complete orientation for all staff.',
      unknown: 'Review orientation program against requirements.',
    },
    stateNotes: [
      'Orientation must be completed BEFORE patient contact per Section 7.8(e)(3)',
      'Must include advance directive training (DNR, COLST) per 18 V.S.A. Section 9701',
      'Abuse and neglect reporting training required per 33 V.S.A. Chapter 69',
      'Infection prevention protocols per Vermont Department of Health guidelines',
      'Document completion with signatures and dates in personnel file',
    ],
  },

  {
    id: 'VT-C2',
    itemId: 'C2',
    stateCode: 'VT',
    title: 'Unlicensed Caregiver Competency',
    description: 'Vermont requires competency assurance for unlicensed caregivers per Section 12',
    severity: 'high',
    regulationCode: '13-110-010-12',
    regulationName: 'Caregiver Services',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you assure competency of unlicensed caregivers before they provide services?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all caregivers have documented competency' },
          { value: 'most', label: 'Most have competency verification' },
          { value: 'no', label: 'No formal competency assurance' },
        ],
        required: true,
        helpText: 'Required per Section 12.2',
      },
      {
        id: 'q2',
        question: 'Do you train caregivers on specific tasks for specific patients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 12.2',
      },
      {
        id: 'q3',
        question: 'Are unlicensed caregivers appropriately supervised by a qualified supervisor?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 12.2',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Unlicensed caregiver competency assurance meets Section 12 requirements.',
      atRisk: 'Some caregivers may lack competency documentation or patient-specific training.',
      nonCompliant: 'No formal competency assurance program. This violates Section 12. Implement immediately.',
      unknown: 'Review caregiver competency process against Section 12 requirements.',
    },
    stateNotes: [
      'Must assure competency of unlicensed caregivers',
      'Train caregivers on specific tasks for specific patients',
      'Ensure appropriate supervision by qualified supervisor',
      'Services must be per plan of care and program standards',
    ],
  },

  {
    id: 'VT-C3',
    itemId: 'C3',
    stateCode: 'VT',
    title: 'Abuse/Neglect/Exploitation Training',
    description: 'Vermont requires training on mandatory reporting requirements',
    severity: 'high',
    regulationCode: '33 V.S.A. 69',
    regulationName: 'Mandatory Reporting',
    agencyName: 'Vermont Adult Protective Services',
    agencyAbbr: 'APS',
    agencyPhone: '(800) 564-1612',
    agencyUrl: 'https://dlp.vermont.gov/aps',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff receive training on recognizing and reporting abuse, neglect, and exploitation of vulnerable adults?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all trained' },
          { value: 'most', label: 'Most trained, some pending' },
          { value: 'no', label: 'No ANE training provided' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report to Adult Protective Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, trained on APS reporting (1-800-564-1612)' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do staff know the 48-hour mandatory reporting requirement?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Home health agencies are mandatory reporters per Section 8.7(a)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on ANE recognition and mandatory reporting requirements.',
      atRisk: 'Some staff may lack ANE training. Complete training for all and ensure familiarity with APS reporting (1-800-564-1612).',
      nonCompliant: 'No ANE training provided. Home health agencies are mandatory reporters per 33 V.S.A. Chapter 69. Reports must be made within 48 hours.',
      unknown: 'Verify all staff have received ANE training.',
    },
    stateNotes: [
      'Home health agencies are mandatory reporters',
      'Report to APS within 48 hours (Section 8.7(a))',
      'Vermont Adult Protective Services Hotline: 1-800-564-1612',
      'Critical incidents must also be reported to DLP Survey and Certification Unit by next business day (Section 8.7)',
    ],
  },

  // ============================================
  // SECTION D: CARE DELIVERY
  // ============================================
  {
    id: 'VT-D1',
    itemId: 'D1',
    stateCode: 'VT',
    title: 'Plan of Care Requirements',
    description: 'Vermont requires person-centered plans of care per Section 17',
    severity: 'high',
    regulationCode: '13-110-010-17',
    regulationName: 'Plan of Care',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are your plans of care person-centered and understandable to patients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 17.3',
      },
      {
        id: 'q3',
        question: 'For non-Medicare programs, do you respond to referrals within 2 business days?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, within 2 business days' },
          { value: 'sometimes', label: 'Sometimes longer' },
          { value: 'no', label: 'No formal timeframe' },
        ],
        helpText: 'Required per Section 17.2',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'equals', questionId: 'q3', value: 'sometimes' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care are person-centered, completed before services begin, and referrals handled within 2 business days per Section 17.',
      atRisk: 'Care plans may be delayed or not person-centered. Review process against Section 17.',
      nonCompliant: 'Services are being provided without written plans of care. This violates Section 17. Immediately create care plans for all patients.',
      unknown: 'Review care plan process against Section 17 requirements.',
    },
    stateNotes: [
      'Medicare-certified services follow Medicare CoPs (Section 17.1)',
      'Non-Medicare programs follow applicable program standards (Section 17.2)',
      'Plans must be person-centered and understandable to layperson (Section 17.3)',
      'Must respond to referrals within 2 business days for non-Medicare (Section 17.2)',
      'Must respond timely to patient requests regarding care plan (Section 17.5)',
    ],
  },

  {
    id: 'VT-D2',
    itemId: 'D2',
    stateCode: 'VT',
    title: 'Patient Rights',
    description: 'Vermont requires comprehensive patient rights per Section 18',
    severity: 'high',
    regulationCode: '13-110-010-18',
    regulationName: 'Patient Rights',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written notice of patient rights during initial visit or before care is furnished?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Section 18.2',
      },
      {
        id: 'q2',
        question: 'Does your admission packet include contact information for:',
        inputType: 'checklist',
        options: [
          { value: 'hca', label: 'Office of the Health Care Advocate' },
          { value: 'ombudsman', label: 'State Long-Term Care Ombudsman (if Choices for Care participant)' },
          { value: 'hotline', label: 'Home Health Hotline' },
          { value: 'agency', label: 'How to contact your agency at all times' },
        ],
        helpText: 'Required per Sections 18.10, 18.11, 18.12',
      },
      {
        id: 'q3',
        question: 'Do you inform patients of charges and payment expectations before care begins?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 18.13',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notice and admission packet meet Section 18 requirements.',
      atRisk: 'Patient rights process may be incomplete. Ensure all required contacts are in admission packet.',
      nonCompliant: 'Not providing written patient rights notice violates Section 18.2. Implement immediately.',
      unknown: 'Review patient rights process against Section 18.',
    },
    stateNotes: [
      'Written rights notice required during initial visit or before care per Section 18.2',
      'Must include Health Care Advocate contact per Section 18.10',
      'Choices for Care participants must receive Ombudsman contact per Section 18.11',
      'Patients have right to participate in care planning per Section 18.7',
      'Must inform patients of charges and payment expectations per Section 18.13',
    ],
  },

  {
    id: 'VT-D3',
    itemId: 'D3',
    stateCode: 'VT',
    title: 'Discharge and Transfer Requirements',
    description: 'Vermont has detailed discharge notice requirements per Section 16',
    severity: 'high',
    regulationCode: '13-110-010-16',
    regulationName: 'Discharge',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is discharge planning initiated at time of admission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per Section 16.2',
      },
      {
        id: 'q2',
        question: 'Do you provide both verbal AND written notice for denials, reductions, or discharges?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 16.3',
      },
      {
        id: 'q3',
        question: 'Do your written notices include appeal information and contact for Health Care Advocate and Ombudsman?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 16.5',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Discharge and notice procedures meet Section 16 requirements.',
      atRisk: 'Discharge notice procedures may be incomplete. Ensure verbal and written notice with appeal information.',
      nonCompliant: 'Discharge planning not initiated at admission. This violates Section 16.2.',
      unknown: 'Review discharge procedures against Section 16.',
    },
    stateNotes: [
      'Discharge planning initiated at admission (Section 16.2)',
      'Verbal AND written notice required (Section 16.3)',
      'Different notice periods: immediately, 2 business days, or 14 calendar days depending on reason (Section 16.4)',
      'Notices must include appeal rights and advocate contacts (Section 16.5)',
      'Must arrange for continuing care when needed (Section 16.7)',
    ],
  },

  // ============================================
  // SECTION E: DOCUMENTATION & RECORDS
  // ============================================
  {
    id: 'VT-E1',
    itemId: 'E1',
    stateCode: 'VT',
    title: 'Patient Record Requirements',
    description: 'Vermont requires comprehensive patient records per Section 25',
    severity: 'high',
    regulationCode: '13-110-010-25',
    regulationName: 'Patient Records',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do patient records include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'history', label: 'Patient history and current condition/status' },
          { value: 'care_plan', label: 'Plan of care' },
          { value: 'visit_notes', label: 'Visit/service documentation' },
          { value: 'assessments', label: 'Clinical assessments' },
          { value: 'advance_directives', label: 'Advance directives (DNR, COLST) if provided' },
          { value: 'physician_orders', label: 'Physician orders' },
          { value: 'medications', label: 'Medication information' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are records maintained confidentially and safeguarded against loss or unauthorized use?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 25.2',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient records meet Section 25 requirements.',
      atRisk: 'Patient records may be missing required elements. Missing: {missing}.',
      nonCompliant: 'Patient records do not meet Section 25 requirements. Immediately update record-keeping practices.',
      unknown: 'Review patient records against Section 25 requirements.',
    },
    stateNotes: [
      'Records must include plan of care and visit documentation per Section 25.1',
      'Must safeguard records against loss or unauthorized use per Section 25.2',
      'COLST (Clinician Orders for Life Sustaining Treatment) per 18 V.S.A. Section 9701',
      'Electronic records must meet data integrity requirements per Section 7.8(h)',
      'Records must be made available to patients upon request per Section 25.3',
    ],
  },

  {
    id: 'VT-E2',
    itemId: 'E2',
    stateCode: 'VT',
    title: 'Record Retention (10 Years)',
    description: 'Vermont requires 10-year record retention per Section 25.4',
    severity: 'high',
    regulationCode: '13-110-010-25.4',
    regulationName: 'Record Retention',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient records after cost report filing?',
        inputType: 'select',
        options: [
          { value: '10plus', label: '10 years or longer (meets VT requirement)' },
          { value: '7to9', label: '7-9 years' },
          { value: 'less7', label: 'Less than 7 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Vermont requires 10 years after month cost report filed per Section 25.4',
      },
      {
        id: 'q2',
        question: 'Do you have arrangements for record retention if the agency discontinues operations?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 25.4',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '10plus' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '7to9', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less7', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets Vermont\'s 10-year requirement per Section 25.4.',
      atRisk: 'Vermont requires 10-year record retention per Section 25.4. Update your policy.',
      nonCompliant: 'Your record retention does not meet Vermont\'s 10-year requirement per Section 25.4.',
      unknown: 'Review record retention requirements per Section 25.4.',
    },
    stateNotes: [
      'Retain records for 10 years after month cost report filed (Section 25.4)',
      'This is longer than the 7-year requirement stated in the original file',
      'Must arrange for retention even if agency discontinues operations',
      'Longer retention may be required by state or federal law',
    ],
  },

  // ============================================
  // SECTION F: QUALITY & COMPLIANCE
  // ============================================
  {
    id: 'VT-F1',
    itemId: 'F1',
    stateCode: 'VT',
    title: 'Quality Assessment and Performance Improvement (QAPI)',
    description: 'Vermont requires a comprehensive QAPI program per Section 19',
    severity: 'high',
    regulationCode: '13-110-010-19',
    regulationName: 'Quality Improvement',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your QAPI program include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'data_collection', label: 'Ongoing measurable data collection system' },
          { value: 'indicators', label: 'Track quality indicators and adverse events' },
          { value: 'identify', label: 'Identify changes that will lead to improvement' },
          { value: 'implement', label: 'Implement quality improvements and corrective actions' },
          { value: 'evaluate', label: 'Evaluate results' },
          { value: 'integrate', label: 'Systematically integrate successful improvements' },
        ],
        required: true,
        helpText: 'Required per Section 19.1',
      },
      {
        id: 'q2',
        question: 'Do your QAPI activities involve direct care staff in identification and planning?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 19.6',
      },
      {
        id: 'q3',
        question: 'Do you monitor patient and family satisfaction and use it for quality improvement?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 19.5',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets Section 19 requirements with data-driven improvement and staff involvement.',
      atRisk: 'QAPI program is incomplete. Missing: {missing}. Enhance program per Section 19.',
      nonCompliant: 'QAPI program does not meet Section 19 requirements. Immediately develop comprehensive data-driven program.',
      unknown: 'Review QAPI program against Section 19 requirements.',
    },
    stateNotes: [
      'Must be data-driven with ongoing measurable data collection (Section 19.1)',
      'Focus on high risk, high-volume, problem-prone areas (Section 19.4)',
      'Involve direct care staff in QI planning (Section 19.6)',
      'Monitor patient/family satisfaction (Section 19.5)',
      'Must participate in DAIL Quality Review processes (Section 19.3)',
    ],
  },

  {
    id: 'VT-F2',
    itemId: 'F2',
    stateCode: 'VT',
    title: 'Critical Incident Reporting',
    description: 'Vermont requires critical incident reporting per Section 8.7',
    severity: 'high',
    regulationCode: '13-110-010-8.7',
    regulationName: 'Incident Reporting',
    agencyName: 'Vermont DLP Survey and Certification Unit',
    agencyAbbr: 'DLP',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dlp.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you report critical incidents to DLP Survey and Certification Unit by the next business day?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always by next business day' },
          { value: 'sometimes', label: 'Sometimes delayed' },
          { value: 'no', label: 'No formal reporting process' },
        ],
        required: true,
        helpText: 'Required per Section 8.7',
      },
      {
        id: 'q2',
        question: 'Do you follow up verbal reports with written reports?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 8.7',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Critical incident reporting meets Section 8.7 requirements with next-business-day reporting.',
      atRisk: 'Critical incident reports should be made by next business day. Review reporting timeline.',
      nonCompliant: 'No formal critical incident reporting process. This violates Section 8.7. Implement immediately.',
      unknown: 'Review critical incident reporting process against Section 8.7.',
    },
    stateNotes: [
      'Critical incidents: death, suicide, poisoning, serious injury requiring medical treatment/hospitalization',
      'May include: equipment failure, medication error, restraint misuse, suspected abuse/neglect/exploitation',
      'Report to DLP Survey and Certification Unit by next business day',
      'Verbal report followed by written summary',
    ],
  },

  {
    id: 'VT-F3',
    itemId: 'F3',
    stateCode: 'VT',
    title: 'Complaint Handling',
    description: 'Vermont requires formal complaint handling per Section 22',
    severity: 'medium',
    regulationCode: '13-110-010-22',
    regulationName: 'Complaints',
    agencyName: 'Vermont Home Health Hotline',
    agencyAbbr: 'DLP',
    agencyPhone: '(800) 564-1612',
    agencyUrl: 'https://dlp.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you respond to all complaints within 2 business days?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 2 business days' },
          { value: 'usually', label: 'Usually, but sometimes longer' },
          { value: 'no', label: 'No formal timeline' },
        ],
        required: true,
        helpText: 'Required per Section 22.2',
      },
      {
        id: 'q2',
        question: 'Do you maintain a complaint log with date, complainant, subject, assigned investigator, and resolution?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 22.3',
      },
      {
        id: 'q3',
        question: 'Do you report unresolved complaints to DLP within 8 business days?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per Section 22.4',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Complaint handling meets Section 22 requirements.',
      atRisk: 'Complaint handling may not meet required timelines or documentation standards.',
      nonCompliant: 'Complaint handling does not meet Section 22 requirements. Implement formal process.',
      unknown: 'Review complaint handling process against Section 22.',
    },
    stateNotes: [
      'Respond to all complaints within 2 business days (Section 22.2)',
      'Maintain complaint log (Section 22.3)',
      'Report unresolved complaints to DLP within 8 business days (Section 22.4)',
      'Notify patient of right to contact Health Care Advocate/Ombudsman if not resolved in 5 business days (Section 22.5)',
      'Furnish patients with Home Health Hotline number (Section 22.1)',
    ],
  },

  {
    id: 'VT-F4',
    itemId: 'F4',
    stateCode: 'VT',
    title: 'Annual Data Reporting',
    description: 'Vermont requires annual data reporting to DAIL per Section 8.10',
    severity: 'medium',
    regulationCode: '13-110-010-8.10',
    regulationName: 'Data Reporting',
    agencyName: 'Vermont DAIL',
    agencyAbbr: 'DAIL',
    agencyPhone: '(802) 241-2401',
    agencyUrl: 'https://dail.vermont.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you collect and report required data to DAIL annually?',
        inputType: 'checklist',
        options: [
          { value: 'complaints', label: 'Complaints' },
          { value: 'waiting_lists', label: 'Individuals on waiting lists' },
          { value: 'ineligible', label: 'Individuals ineligible for services' },
          { value: 'under_65', label: 'Patients under 65' },
          { value: 'over_65', label: 'Patients 65 and older' },
          { value: 'visits', label: 'Total visits and visiting hours' },
          { value: 'charitable', label: 'Charitable and subsidized programs' },
        ],
        required: true,
        helpText: 'Required per Section 8.10',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual data reporting meets Section 8.10 requirements.',
      atRisk: 'Annual data reporting may be incomplete. Review Section 8.10 requirements.',
      nonCompliant: 'Annual data reporting does not meet Section 8.10 requirements.',
      unknown: 'Review data reporting against Section 8.10.',
    },
    stateNotes: [
      'Annual data submission to DAIL required per Section 8.10',
      'Must report patient demographics by age category per Section 8.10(d-e)',
      'Report waiting list and ineligibility data per Section 8.10(b-c)',
      'Track charitable and subsidized program utilization per Section 8.10(g)',
      'Data used by DAIL to assess home health needs statewide',
    ],
  },
];

export default VERMONT_QUESTIONS;